// ===========================================
// Lesson Detail Page Helpers
// ===========================================

import { Lesson } from '../../../api/lessons.api';
import { RelatedTerm } from './lessonDetail.types';
import { parseUTCDate } from '../../../utils/dateUtils';

/**
 * The sibling lesson rows for this lesson across terms (same name, teacher,
 * room, day, time). A "multi-term" lesson is N separate Lesson rows — one per
 * term — so a lesson that spans Term 1 + Term 2 is two rows with different
 * termIds. Includes the lesson itself.
 */
function siblingTermLessons(lesson: Lesson, allLessons: Lesson[]): Lesson[] {
  return allLessons.filter(
    (l) =>
      l.isActive &&
      l.name === lesson.name &&
      l.teacherId === lesson.teacherId &&
      l.roomId === lesson.roomId &&
      l.dayOfWeek === lesson.dayOfWeek &&
      l.startTime === lesson.startTime &&
      l.endTime === lesson.endTime
  );
}

/**
 * Find all terms this lesson spans (same name, teacher, room, day, time)
 */
export function findRelatedTerms(lesson: Lesson, allLessons: Lesson[]): RelatedTerm[] {
  return siblingTermLessons(lesson, allLessons)
    .map((l) => ({ id: l.id, termId: l.termId, termName: l.Term.name }))
    .sort((a, b) => a.termName.localeCompare(b.termName));
}

/**
 * Resolve the lesson ROW to act on for term-driven actions (assign replacement,
 * reschedule). A lesson can exist as separate rows across multiple terms; the
 * page shows whichever row the URL points at, which may be a PAST term. These
 * actions should default to the CURRENT term (today within its term dates), so
 * the week picker shows the right dates AND the mutation targets the matching
 * row (each term row has its own id).
 *
 * Falls back to the passed lesson when no sibling term is current (e.g. between
 * terms, or a single-term lesson) so behaviour is unchanged in those cases.
 */
export function findCurrentTermLesson(lesson: Lesson, allLessons: Lesson[]): Lesson {
  const siblings = siblingTermLessons(lesson, allLessons);
  // Only one row (or none resolvable) → nothing to switch to.
  if (siblings.length <= 1) return lesson;

  const now = new Date();
  // Terms don't overlap, so at most one sibling is current; `.find` returns it.
  const current = siblings.find((l) => {
    if (!l.Term?.startDate || !l.Term?.endDate) return false;
    const start = parseUTCDate(l.Term.startDate);
    // Term dates are stored at UTC midnight; treat the END date inclusively
    // through the whole final day so a term ending "today" is still current —
    // matching the backend's getCurrentTerm (endDate >= end-of-today).
    const end = parseUTCDate(l.Term.endDate);
    end.setUTCHours(23, 59, 59, 999);
    return start <= now && now <= end;
  });

  return current ?? lesson;
}
